import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetCloudAccountsQuery, useUpgradeApplianceMutation } from '../../../api';
import { useBulkAndDialogs } from './useBulkAndDialogs';
import { useMigrationAppliancePolling } from './useMigrationAppliancePolling';
import { useNotificationContext } from '../../../providers';
import { handleError } from '../../../lib/helpers';
import { CLOUD_TYPE } from '../../../constants';

export const useMigrationAppliancesTable = ({
    selectedRowKeys: externalSelectedRowKeys,
    onSelectedRowKeysChange,
    bulkDeleteTriggerRef,
    onBulkActionSuccess,
}) => {
    const { notifySuccess, notifyError } = useNotificationContext();

    // Set initial pagination properties
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0,
    });

    const [userOrg, setUserOrg] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedApplianceId, setSelectedApplianceId] = useState(null);
    const { state } = useLocation();
    const initialApplianceIdRef = useRef(state?.applianceId ?? null);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [selectedCloudAccountName, setSelectedCloudAccountName] = useState('');

    // Network Troubleshooting Modal state
    const [troubleshootingModalOpen, setTroubleshootingModalOpen] = useState(false);
    const [selectedTroubleshootingApplianceId, setSelectedTroubleshootingApplianceId] = useState(null);
    const [selectedTroubleshootingApplianceName, setSelectedTroubleshootingApplianceName] = useState('');

    const [polledApplianceUpdates, setPolledApplianceUpdates] = useState(new Map());
    const [deletedApplianceIds, setDeletedApplianceIds] = useState(new Set());
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Track which process type is active for each appliance (upgrade / deploy)
    const activeProcessTypeRef = useRef(new Map());
    // Ref to prevent multiple rapid redeploy calls
    const redeployTimerRef = useRef(null);
    const effectiveSelectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : selectedRowKeys;
    const setEffectiveSelectedRowKeys = onSelectedRowKeysChange || setSelectedRowKeys;

    // RTKQ hooks
    const [upgradeAppliance, { isLoading: isUpgrading }] = useUpgradeApplianceMutation();
    const {
        data: migrationAppliancesData,
        isLoading,
        isFetching,
        refetch,
    } = useGetCloudAccountsQuery(
        {
            userOrg,
            size: pagination.pageSize,
            // page number from the API is 0 indexed, so we need to subtract 1 from the current Pagination page
            page: pagination.current - 1,
        },
        {
            skip: userOrg === null,
            refetchOnMountOrArgChange: true,
            refetchOnReconnect: true,
            keepUnusedDataFor: 0,
        },
    );

    const baseTableData = useMemo(() => {
        if (!migrationAppliancesData?.content) return [];
        return migrationAppliancesData.content.filter(
            (appliance) => appliance && !deletedApplianceIds.has(appliance.id),
        );
    }, [migrationAppliancesData, deletedApplianceIds]);

    // Auto-open drawer when navigated from a link with applianceId=<id>
    useEffect(() => {
        const linkedId = initialApplianceIdRef.current;
        if (!linkedId || isLoading || baseTableData.length === 0) return;
        initialApplianceIdRef.current = null;
        setSelectedApplianceId(linkedId);
        setDrawerOpen(true);
    }, [baseTableData, isLoading]);

    const getAppliancesByIds = useCallback(
        (applianceIds) => {
            const allAppliances = [];
            baseTableData.forEach((item) => {
                if (item.isGroup && item.children) {
                    item.children.forEach((child) => {
                        if (applianceIds.includes(child.id)) allAppliances.push(child);
                    });
                } else if (!item.isGroup && applianceIds.includes(item.id)) {
                    allAppliances.push(item);
                }
            });
            return allAppliances;
        },
        [baseTableData],
    );

    const updatePolledAppliance = useCallback(
        (applianceId, updatedApplianceData) => {
            setPolledApplianceUpdates((prev) => {
                const newMap = new Map(prev);
                newMap.set(applianceId, updatedApplianceData);
                return newMap;
            });
            // Get the active process type for this appliance (upgrade or deploy)
            const processType = activeProcessTypeRef.current.get(applianceId);

            // Only check the state relevant to the active process type
            if (processType === 'upgrade') {
                const upgradeState = updatedApplianceData?.upgrade_state;

                if (upgradeState === 'success') {
                    notifySuccess({ description: `Migration appliance upgrade successful` });
                    // Clear the process type tracking since it's complete
                    activeProcessTypeRef.current.delete(applianceId);
                    setTimeout(() => {
                        refetch();
                    }, 1500);
                } else if (upgradeState === 'error') {
                    notifyError({ description: `Error while upgrading the appliance` });
                    // Clear the process type tracking since it's complete
                    activeProcessTypeRef.current.delete(applianceId);
                    setTimeout(() => {
                        refetch();
                    }, 1500);
                }
            } else if (processType === 'deploy') {
                const processState = updatedApplianceData?.process_state;

                if (processState === 'success') {
                    notifySuccess({ description: `Migration appliance deploy successful` });
                    // Clear the process type tracking since it's complete
                    activeProcessTypeRef.current.delete(applianceId);
                    setTimeout(() => {
                        refetch();
                    }, 1500);
                } else if (processState === 'error') {
                    notifyError({ description: `Error while deploying the appliance` });
                    // Clear the process type tracking since it's complete
                    activeProcessTypeRef.current.delete(applianceId);
                    setTimeout(() => {
                        refetch();
                    }, 1500);
                }
            }
        },
        [notifySuccess, notifyError, refetch],
    );

    const tableData = useMemo(() => {
        return baseTableData
            .filter((cloudAccount) => !deletedApplianceIds.has(cloudAccount.id)) // Filter out deleted items
            .map((cloudAccount) => {
                // Get polled update by appliance ID (not cloud account ID)
                const applianceId = cloudAccount?.appliance?.id;
                const polledApplianceUpdate = applianceId ? polledApplianceUpdates.get(applianceId) : null;

                // If there's a polled update, merge it into the nested appliance object
                if (polledApplianceUpdate && cloudAccount.appliance) {
                    return {
                        ...cloudAccount,
                        appliance: {
                            ...cloudAccount.appliance,
                            ...polledApplianceUpdate,
                        },
                    };
                }

                return cloudAccount;
            });
    }, [baseTableData, polledApplianceUpdates, deletedApplianceIds]);

    // Auto-detect process type for appliances that are already in progress (e.g., after page refresh)
    useEffect(() => {
        if (!baseTableData) {
            return;
        }

        baseTableData.forEach((cloudAccount) => {
            const appliance = cloudAccount.appliance;
            if (!appliance || !appliance.id) {
                return;
            }

            const applianceId = appliance.id;

            // Skip if we already know the process type
            if (activeProcessTypeRef.current.has(applianceId)) {
                return;
            }
            const upgradeState = appliance.upgrade_state;
            const processState = appliance.process_state;

            if (upgradeState && ['pending', 'running'].includes(upgradeState)) {
                // Upgrade is active
                activeProcessTypeRef.current.set(applianceId, 'upgrade');
            } else if (processState && ['pending', 'running'].includes(processState)) {
                // Deploy is active
                activeProcessTypeRef.current.set(applianceId, 'deploy');
            }
        });
    }, [baseTableData]);

    // Set up polling for appliances that need it
    const { pollingCount, startPolling, stopPolling } = useMigrationAppliancePolling(
        baseTableData,
        updatePolledAppliance,
    );

    useEffect(() => {
        let storedUserOrg = sessionStorage.getItem('userOrg');
        setUserOrg(storedUserOrg);
    }, []);

    // Update pagination when data changes
    useEffect(() => {
        if (migrationAppliancesData?.page) {
            setPagination((prev) => ({
                ...prev,
                total: migrationAppliancesData.page.totalElements,
                // page number from the API is 0 indexed, so we need to add 1 to it to match the Pagination UI.
                current: migrationAppliancesData.page.number + 1,
            }));
        }
    }, [migrationAppliancesData]);

    // Register refresh callback with the legacy module, even if it loads later
    useEffect(() => {
        if (!refetch) return;

        const register = () => {
            if (window.registerCloudTableRefresh) {
                window.registerCloudTableRefresh(() => {
                    // slight delay can help with eventual consistency after creation
                    setTimeout(() => refetch(), 800);
                });
                return true;
            }
            return false;
        };

        // Attempt immediate registration
        let registered = register();

        // Listen for legacy module readiness
        const onReady = () => {
            if (!registered) {
                registered = register();
            }
        };
        window.addEventListener('CloudListModuleReady', onReady);

        return () => {
            window.removeEventListener('CloudListModuleReady', onReady);
        };
    }, [refetch]);

    // Handle row click to open drawer
    const handleRowClick = useCallback(
        (e, record) => {
            if (!hasDetailsDrawer(record)) {
                return;
            }
            if (record && record.id) {
                setSelectedApplianceId(record.id);
                setDrawerOpen((oState) => !oState);
            }
        },
        [selectedApplianceId],
    );

    // Handle pagination change
    const handlePaginationChange = useCallback((page, pageSize) => {
        setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: pageSize,
        }));
    }, []);

    // Open Delete Migration Appliance Modal
    const handleOpenDeleteModal = useCallback((id, name) => {
        setRecordToDelete(id);
        setSelectedCloudAccountName(name);
        setDeleteModalOpen(true);
    }, []);

    // Handler when upgrade menu item is clicked
    const handleUpgradeAppliance = useCallback(
        async (record) => {
            let applianceId = record?.appliance?.id;
            await upgradeAppliance({ applianceId })
                .unwrap()
                .then(() => {
                    notifySuccess({ message: 'Success', description: 'Appliance upgrade is in Progress' });
                    updatePolledAppliance(applianceId, { ...record.appliance, upgrade_state: 'running' });
                    updateUpgradeProgress(applianceId);
                })
                .catch((error) => {
                    refetch();
                    notifyError({ message: 'Error', description: `Error while upgrading appliance. ${error.message}` });
                });
        },
        [updatePolledAppliance, upgradeAppliance, notifySuccess, notifyError, refetch, updateUpgradeProgress],
    );

    // Check if the record has a details drawer
    const hasDetailsDrawer = useCallback(
        (record) => {
            if (
                (record && record?.type === CLOUD_TYPE.VSPHERE) ||
                record?.type === CLOUD_TYPE.OPENSHIFT ||
                record?.type === CLOUD_TYPE.OPENSTACK ||
                record?.type === CLOUD_TYPE.NC2 ||
                record?.type === CLOUD_TYPE.HPE_VME ||
                record?.type === CLOUD_TYPE.HYPERV
            ) {
                return true;
            }

            if (record?.appliance?.state === 'success') {
                return true;
            }

            return false;
        },
        [migrationAppliancesData],
    );

    // Handle Row selection Log
    const onSelectChange = useCallback((newSelectedRowKeys, selectedRows) => {
        const currentSelected = new Set(effectiveSelectedRowKeys);
        const newSelected = new Set(newSelectedRowKeys);

        // Find what was added or removed
        const added = newSelectedRowKeys.filter((key) => !currentSelected.has(key));
        const removed = effectiveSelectedRowKeys.filter((key) => !newSelected.has(key));
    }, []);

    // Row selection configuration
    const rowSelection = useMemo(
        () => ({
            selectedRowKeys,
            // onChange: onSelectChange,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => {
                return {
                    name: record.key,
                };
            },
        }),
        [selectedRowKeys, onSelectChange],
    );

    // Handle opening troubleshooting modal
    const handleOpenTroubleshootingModal = useCallback((applianceId, applianceName) => {
        setSelectedTroubleshootingApplianceId(applianceId);
        setSelectedTroubleshootingApplianceName(applianceName);
        setTroubleshootingModalOpen(true);
    }, []);

    // Handle closing troubleshooting modal
    const handleCloseTroubleshootingModal = useCallback(() => {
        setTroubleshootingModalOpen(false);
        setSelectedTroubleshootingApplianceId(null);
        setSelectedTroubleshootingApplianceName('');
    }, []);

    const updateUpgradeProgress = useCallback(
        (applianceId) => {
            // Mark this appliance as being tracked for upgrade
            activeProcessTypeRef.current.set(applianceId, 'upgrade');
            // Start polling - the updatePolledAppliance callback will handle state changes
            startPolling(applianceId);
        },
        [startPolling],
    );

    // Handle redeploy migration appliance - triggers backbone setup modal
    const handleRedeployAppliance = useCallback(
        (cloudAccountId) => {
            if (redeployTimerRef.current) {
                return; // Already processing
            }

            // Set a flag to prevent multiple calls
            redeployTimerRef.current = setTimeout(() => {
                redeployTimerRef.current = null;
            }, 1000); // 1 second cooldown

            if (window.setupAppliance) {
                window.setupAppliance(cloudAccountId);
            } else {
                notifyError({
                    message: 'Error',
                    description: 'Unable to open appliance setup. Please refresh the page and try again.',
                });
            }
        },
        [notifyError],
    );

    // Bulk and dialogs state/handlers
    const {
        deleteConfirmOpen,
        appliancesToDelete,
        isDeleting,
        handleBulkDeleteTrigger,
        handleDeleteCancel,
        deleteCloudAccount,
        openDeleteSingleCloudAccountModal,
        handleBulkDeleteConfirm,
    } = useBulkAndDialogs({
        notifySuccess,
        notifyError,
        getAppliancesByIds,
        bulkDeleteTriggerRef,
        userOrg,
        stopPolling,
        setDeletedApplianceIds,
        onBulkActionSuccess,
    });

    const handleDeleteCloudAccount = useCallback(async () => {
        try {
            await deleteCloudAccount(recordToDelete);
            notifySuccess({ description: `Cloud Account Deleted Successfully` });
            // Clean up tracked process type for deleted appliance
            if (recordToDelete) {
                activeProcessTypeRef.current.delete(recordToDelete);
            }
            refetch?.();
            setRecordToDelete(null);
            setEffectiveSelectedRowKeys([]);
        } catch (error) {
            const errorMsg = 'Failed to Delete Cloud Account';
            handleError(error, notifyError, errorMsg);
            setRecordToDelete(null);
            setEffectiveSelectedRowKeys([]);
        }
    }, [recordToDelete, deleteCloudAccount, notifySuccess, notifyError, refetch]);

    return {
        // Data
        tableData,
        appliancesToDelete: appliancesToDelete || [],

        // Loading States
        isLoading,
        isFetching,
        isDeleting,
        isBulkDeleteing: isDeleting,

        // State
        drawerOpen,
        selectedApplianceId,
        selectedCloudAccountName,
        pagination,
        deleteModalOpen,
        bulkDeleteModalOpen: deleteConfirmOpen,

        // Network Troubleshooting Modal state
        troubleshootingModalOpen,
        selectedTroubleshootingApplianceId,
        selectedTroubleshootingApplianceName,

        // Handlers
        handleRowClick,
        onSelectChange,
        handlePaginationChange,
        handleOpenDeleteModal,
        handleUpgradeAppliance,
        handleRedeployAppliance,
        handleOpenTroubleshootingModal,
        handleCloseTroubleshootingModal,
        handleDeleteCancel,
        handleDeleteCloudAccount,
        recordToDelete,
        setRecordToDelete,
        openDeleteSingleCloudAccountModal,
        handleBulkDelete: handleBulkDeleteTrigger,
        handleBulkDeleteConfirm: useCallback(async () => {
            await handleBulkDeleteConfirm(appliancesToDelete, refetch, setEffectiveSelectedRowKeys);
        }, [handleBulkDeleteConfirm, refetch, setEffectiveSelectedRowKeys, getAppliancesByIds]),
        handleBulkDeleteCancel: handleDeleteCancel,

        // Configuration
        rowSelection,
        hasDetailsDrawer,

        // Drawer Handlers
        setDrawerOpen,
        setSelectedApplianceId,
        setDeleteModalOpen,

        // Refresh function
        refetch,

        // Polling
        pollingCount,
    };
};
